import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { ContractAppendSections, normalizeSections } from "./ContractAppendSections";
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";

/**
 * Read-only or edit view of the generated company profile / onboarding document.
 * Append sections (paragraph/table) render before the place / palette footer.
 */
export function CompanyProfileDocument({
  project,
  getAttr,
  docSections,
  setDocSections,
  isDocEditMode,
  isDocGenerating,
  appendSectionsEditable = false,
  /** When true, skip the in-document logo / title row (parent provides a shell header). */
  omitOuterBranding = false,
  /** Advisory firm name (AdvisoryFirm.firmName); omitted from the header when not known. */
  advisorName = "",
}) {
  const companyLogoUrl = getAttr("companyLogo");
  const companyFont = getAttr("companyFont") || "inherit";
  const rawColor = getAttr("companyColorPalette") || "#1e293b";
  let companyColor = rawColor;
  try {
    const parsed = JSON.parse(rawColor);
    if (parsed && typeof parsed === 'object') {
      companyColor = parsed.primary || "#1e293b";
    }
  } catch {
    // backward compatibility
    companyColor = rawColor;
  }
  const abbrev = getAttr("companyAbbreviation") || "";

  let hlParams = [];
  try {
    hlParams = JSON.parse(docSections.highLevel || "[]");
  } catch {
    hlParams = [];
  }

  let appReqs = [];
  try {
    appReqs = JSON.parse(docSections.applicable || "[]");
  } catch {
    appReqs = [];
  }

  const appendSections = normalizeSections(docSections.appendSections);

  if (isDocEditMode) {
    return /* @__PURE__ */ jsxs("div", {
      className: "space-y-6",
      children: [
        /* @__PURE__ */ jsxs("div", {
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 block",
              children: "Executive Summary",
            }),
            /* @__PURE__ */ jsx("textarea", {
              rows: 16,
              value: docSections.execSummary,
              onChange: (e) => setDocSections((s) => ({ ...s, execSummary: e.target.value })),
              className:
                "w-full p-4 border border-slate-200 rounded-xl text-sm font-medium outline-none resize-none focus:ring-2 focus:ring-blue-200",
            }),
          ],
        }),
        /* @__PURE__ */ jsxs("div", {
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 block",
              children: "Organization Structure",
            }),
            /* @__PURE__ */ jsx("textarea", {
              rows: 8,
              value: docSections.orgStructure,
              onChange: (e) => setDocSections((s) => ({ ...s, orgStructure: e.target.value })),
              className:
                "w-full p-4 border border-slate-200 rounded-xl text-sm font-medium outline-none resize-none focus:ring-2 focus:ring-blue-200",
            }),
          ],
        }),
        /* @__PURE__ */ jsxs("div", {
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 block",
              children: "High Level Organizational Parameters",
            }),
            /* @__PURE__ */ jsx("div", {
              className: "space-y-2",
              children: hlParams.map((p, i) =>
                /* @__PURE__ */ jsxs("div", {
                  className: "flex gap-2",
                  children: [
                    /* @__PURE__ */ jsx("input", {
                      value: p.param,
                      readOnly: true,
                      className: "w-48 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold",
                    }),
                    /* @__PURE__ */ jsx("input", {
                      value: p.value,
                      onChange: (e) => {
                        const u = [...hlParams];
                        u[i] = { ...u[i], value: e.target.value };
                        setDocSections((s) => ({ ...s, highLevel: JSON.stringify(u) }));
                      },
                      className:
                        "flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200",
                    }),
                  ],
                }, i)
              ),
            }),
          ],
        }),
        /* @__PURE__ */ jsxs("div", {
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 block",
              children: "GICS Industry Classification",
            }),
            /* @__PURE__ */ jsx("input", {
              value: docSections.gics,
              onChange: (e) => setDocSections((s) => ({ ...s, gics: e.target.value })),
              className:
                "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200",
            }),
          ],
        }),
        /* @__PURE__ */ jsxs("div", {
          children: [
            /* @__PURE__ */ jsx("label", {
              className: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1 block",
              children: "Applicable Requirements",
            }),
            /* @__PURE__ */ jsx("div", {
              className: "space-y-2",
              children: [appReqs.length === 0 && /* @__PURE__ */ jsx("p", {
                "data-testid": "applicable-requirements-empty",
                className: "text-sm text-slate-500",
                children: "No applicable requirements were identified; add them manually.",
              }, "empty"), ...appReqs.map((r, i) =>
                /* @__PURE__ */ jsxs("div", {
                  className: "flex gap-2 items-center",
                  children: [
                    /* @__PURE__ */ jsx("span", {
                      className: "w-8 text-center text-xs font-bold text-slate-400",
                      children: r.sr,
                    }),
                    /* @__PURE__ */ jsx("input", {
                      value: r.title,
                      onChange: (e) => {
                        const u = [...appReqs];
                        u[i] = { ...u[i], title: e.target.value };
                        setDocSections((s) => ({ ...s, applicable: JSON.stringify(u) }));
                      },
                      className:
                        "flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-200",
                    }),
                    /* @__PURE__ */ jsx("input", {
                      value: r.reqCount,
                      onChange: (e) => {
                        const u = [...appReqs];
                        u[i] = { ...u[i], reqCount: e.target.value };
                        setDocSections((s) => ({ ...s, applicable: JSON.stringify(u) }));
                      },
                      placeholder: "# Req",
                      className:
                        "w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm text-center outline-none focus:ring-2 focus:ring-blue-200",
                    }),
                    /* @__PURE__ */ jsx("input", {
                      value: r.controlCount,
                      onChange: (e) => {
                        const u = [...appReqs];
                        u[i] = { ...u[i], controlCount: e.target.value };
                        setDocSections((s) => ({ ...s, applicable: JSON.stringify(u) }));
                      },
                      placeholder: "# Ctrl",
                      className:
                        "w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm text-center outline-none focus:ring-2 focus:ring-blue-200",
                    }),
                  ],
                }, i)
              )],
            }),
            /* @__PURE__ */ jsx("button", {
              type: "button",
              "data-testid": "applicable-requirements-add",
              onClick: () => setDocSections((s) => ({
                ...s,
                applicable: JSON.stringify([...appReqs, { sr: appReqs.length + 1, title: "", reqCount: "", controlCount: "" }]),
              })),
              className: "mt-2 px-3 py-1.5 bg-white border border-slate-200 text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-50",
              children: "+ Add requirement",
            }),
          ],
        }),
        (appendSections.length > 0 || appendSectionsEditable) &&
          /* @__PURE__ */ jsx(ContractAppendSections, {
            sections: appendSections,
            onChange: (next) => setDocSections((s) => ({ ...s, appendSections: next })),
            editable: appendSectionsEditable,
            numberOffset: 6,
          }),
      ],
    });
  }

  return /* @__PURE__ */ jsxs("div", {
    className: "space-y-8",
    style: { fontFamily: companyFont, color: companyColor },
    children: [
      !omitOuterBranding &&
        /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsxs("div", {
              className: "flex items-start justify-between",
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-4",
                  children: [
                    companyLogoUrl
                      ? /* @__PURE__ */ jsx("img", {
                          src: companyLogoUrl,
                          alt: "Company Logo",
                          className: "h-12 w-auto object-contain",
                        })
                      : /* @__PURE__ */ jsx("div", {
                          className:
                            "w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-lg font-black text-slate-400",
                          children: (project.companyName || "C")[0],
                        }),
                    /* @__PURE__ */ jsxs("div", {
                      children: [
                        /* @__PURE__ */ jsx("p", {
                          className: "text-lg font-black",
                          style: { color: companyColor },
                          children: getAttr("legalName") || project.companyName,
                        }),
                        abbrev &&
                          /* @__PURE__ */ jsx("p", {
                            className: "text-xs font-semibold text-slate-400 uppercase tracking-widest",
                            children: abbrev,
                          }),
                      ],
                    }),
                  ],
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "text-right",
                  children: [
                    advisorName &&
                      /* @__PURE__ */ jsx("p", {
                        className: "text-sm font-bold text-slate-600",
                        children: advisorName,
                      }),
                    /* @__PURE__ */ jsx("p", {
                      className: "text-xs text-slate-400 mt-1",
                      children: formatDdMmYyyy(Date.now()),
                    }),
                    /* @__PURE__ */ jsx("p", {
                      className: "text-[9px] font-black uppercase tracking-[0.2em] text-rose-500 mt-1",
                      children: "Confidential",
                    }),
                  ],
                }),
              ],
            }),
            /* @__PURE__ */ jsx("div", { className: "border-t border-slate-200" }),
          ],
        }),
      isDocGenerating
        ? /* @__PURE__ */ jsxs("div", {
            className: "flex flex-col items-center justify-center py-16 gap-4",
            children: [
              /* @__PURE__ */ jsx("div", {
                className: "w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin",
              }),
              /* @__PURE__ */ jsx("p", {
                className: "text-sm font-semibold text-slate-500 animate-pulse",
                children: "Generating document with AI...",
              }),
            ],
          })
        : /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("h3", {
                    className: "text-sm font-black uppercase tracking-[0.15em] text-slate-400 mb-3",
                    children: "Executive Summary",
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "bg-slate-50/50 border border-slate-100 rounded-xl p-5",
                    children: /* @__PURE__ */ jsx("p", {
                      className: "text-sm leading-relaxed text-slate-700 whitespace-pre-wrap",
                      children: docSections.execSummary || "-",
                    }),
                  }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("h3", {
                    className: "text-sm font-black uppercase tracking-[0.15em] text-slate-400 mb-3",
                    children: "Organization Structure",
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "bg-slate-50/50 border border-slate-100 rounded-xl p-5",
                    children: /* @__PURE__ */ jsx("p", {
                      className: "text-sm leading-relaxed text-slate-700 whitespace-pre-wrap",
                      children: docSections.orgStructure || "-",
                    }),
                  }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("h3", {
                    className: "text-sm font-black uppercase tracking-[0.15em] text-slate-400 mb-3",
                    children: "High Level Organizational Parameters",
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "overflow-hidden rounded-xl border border-slate-200",
                    children: /* @__PURE__ */ jsxs("table", {
                      className: "w-full text-sm",
                      children: [
                        /* @__PURE__ */ jsx("thead", {
                          children: /* @__PURE__ */ jsxs("tr", {
                            className: "bg-slate-50",
                            children: [
                              /* @__PURE__ */ jsx("th", {
                                className:
                                  "px-5 py-3 text-left text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200",
                                children: "Parameter",
                              }),
                              /* @__PURE__ */ jsx("th", {
                                className:
                                  "px-5 py-3 text-left text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200",
                                children: "Value",
                              }),
                            ],
                          }),
                        }),
                        /* @__PURE__ */ jsx("tbody", {
                          children: hlParams.map((p, i) =>
                            /* @__PURE__ */ jsxs(
                              "tr",
                              {
                                className: i % 2 === 0 ? "bg-white" : "bg-slate-50/50",
                                children: [
                                  /* @__PURE__ */ jsx("td", {
                                    className: "px-5 py-3 font-semibold text-slate-700 border-b border-slate-100",
                                    children: p.param,
                                  }),
                                  /* @__PURE__ */ jsx("td", {
                                    className: "px-5 py-3 text-slate-600 border-b border-slate-100",
                                    children: p.value || "#",
                                  }),
                                ],
                              },
                              i
                            )
                          ),
                        }),
                      ],
                    }),
                  }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("h3", {
                    className: "text-sm font-black uppercase tracking-[0.15em] text-slate-400 mb-3",
                    children: "GICS Industry Classification",
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className:
                      "inline-block px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-sm font-bold text-blue-700",
                    children: docSections.gics || "-",
                  }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("h3", {
                    className: "text-sm font-black uppercase tracking-[0.15em] text-slate-400 mb-3",
                    children: "Applicable Requirements of Legal, Statutory & Standards",
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "overflow-hidden rounded-xl border border-slate-200",
                    children: /* @__PURE__ */ jsxs("table", {
                      className: "w-full text-sm",
                      children: [
                        /* @__PURE__ */ jsx("thead", {
                          children: /* @__PURE__ */ jsxs("tr", {
                            className: "bg-slate-50",
                            children: [
                              /* @__PURE__ */ jsx("th", {
                                className:
                                  "px-4 py-3 text-center text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 w-16",
                                children: "Sr. No.",
                              }),
                              /* @__PURE__ */ jsx("th", {
                                className:
                                  "px-4 py-3 text-left text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200",
                                children: "Title",
                              }),
                              /* @__PURE__ */ jsx("th", {
                                className:
                                  "px-4 py-3 text-center text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 w-36",
                                children: "Count of Requirements",
                              }),
                              /* @__PURE__ */ jsx("th", {
                                className:
                                  "px-4 py-3 text-center text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 w-36",
                                children: "Count of Controls",
                              }),
                            ],
                          }),
                        }),
                        /* @__PURE__ */ jsx("tbody", {
                          children: appReqs.length === 0 ? /* @__PURE__ */ jsx("tr", {
                            children: /* @__PURE__ */ jsx("td", {
                              colSpan: 4,
                              "data-testid": "applicable-requirements-empty",
                              className: "px-4 py-6 text-center text-sm text-slate-500",
                              children: "No applicable requirements were identified; add them manually.",
                            }),
                          }) : appReqs.map((r, i) =>
                            /* @__PURE__ */ jsxs(
                              "tr",
                              {
                                className: i % 2 === 0 ? "bg-white" : "bg-slate-50/50",
                                children: [
                                  /* @__PURE__ */ jsx("td", {
                                    className:
                                      "px-4 py-3 text-center font-semibold text-slate-500 border-b border-slate-100",
                                    children: r.sr,
                                  }),
                                  /* @__PURE__ */ jsx("td", {
                                    className: "px-4 py-3 font-semibold text-slate-700 border-b border-slate-100",
                                    children: r.title,
                                  }),
                                  /* @__PURE__ */ jsx("td", {
                                    className: "px-4 py-3 text-center text-slate-600 border-b border-slate-100",
                                    children: r.reqCount || "--",
                                  }),
                                  /* @__PURE__ */ jsx("td", {
                                    className: "px-4 py-3 text-center text-slate-600 border-b border-slate-100",
                                    children: r.controlCount || "--",
                                  }),
                                ],
                              },
                              i
                            )
                          ),
                        }),
                      ],
                    }),
                  }),
                ],
              }),
              (appendSections.length > 0 || appendSectionsEditable) &&
                /* @__PURE__ */ jsx(ContractAppendSections, {
                  sections: appendSections,
                  onChange: (next) => setDocSections((s) => ({ ...s, appendSections: next })),
                  editable: appendSectionsEditable,
                  numberOffset: 6,
                }),
              /* @__PURE__ */ jsxs("div", {
                className: "border-t border-slate-200 pt-6 text-center space-y-1",
                children: [
                  /* @__PURE__ */ jsx("p", {
                    className: "text-xs text-slate-400 font-medium",
                    children: getAttr("hqCity") || "Place",
                  }),
                  /* @__PURE__ */ jsx("p", {
                    className: "text-[10px] text-slate-300 italic",
                    children: "P.S. Output is rendered in client's font and color palette",
                  }),
                ],
              }),
            ],
          }),
    ],
  });
}
